import React, { useState, useEffect } from "react";
import Navber from "./Navber.jsx";
import api from "../Api.js";
import dayjs from "dayjs";
import Fullspin from "./Helping Component/Fullscreeenspinner.jsx";
import "./TripForm.css";

export default function TripForm() {
  const [days, setDays] = useState([{ day: 1, value: "" }]);
  const [Itinerary, setItinerary] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [verificationError, setVerificationError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [animatedFields, setAnimatedFields] = useState({});

  const [formData, setFormData] = useState({
    destination: "",
    pickup: "",
    drop: "",
    adult: null,
    child: null,
    infant: null,
    days: null,
    nights: null,
    startDate: null,
    name: "",
    email: "",
    whatsapp: "",
    special: "",
    travel: "",
    food: ""
  });

  const [planType, setPlanType] = useState("budget_itinerary");

  // Countdown timer for OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
    // Trigger animation
    setAnimatedFields(prev => ({ ...prev, [name]: true }));
    setTimeout(() => setAnimatedFields(prev => ({ ...prev, [name]: false })), 300);
  };

  const handlePlanChange = (type) => {
    setPlanType(type);
  };

  const handleAddDay = () => {
    setDays((prev) => [
      ...prev,
      { day: prev.length + 1, value: "" },
    ]);
  };

  const handleChanges = (index, newValue) => {
    const updated = [...days];
    updated[index].value = newValue;
    setDays(updated);
    const iti = updated.map(item => item.value);
    setItinerary(iti);
  };

  const validateWhatsAppNumber = (number) => {
    const whatsappRegex = /^[0-9]{10}$/;
    return whatsappRegex.test(number);
  };

  // Send OTP API Integration
  const handleSendOTP = async () => {
    if (!formData.whatsapp) {
      setVerificationError("Please enter WhatsApp number");
      return;
    }
    
    if (!validateWhatsAppNumber(formData.whatsapp)) {
      setVerificationError("Please enter a valid 10-digit WhatsApp number");
      return;
    }
    
    setVerificationError("");
    setLoading(true);
    
    try {
      // API call to send OTP
      const response = await api.post("/sendto-otp", {
        phone: formData.whatsapp
      });
      
      console.log("Send OTP Response:", response);
      
      if (response?.data?.status === true || response?.status === 200) {
        setOtpSent(true);
        setShowOtpInput(true);
        setCountdown(60);
        // Show success message without alert for better UX
        setVerificationError("");
        // Optional: Show success toast/notification
        console.log(`OTP sent to WhatsApp number ${formData.whatsapp}`);
      } else {
        setVerificationError(response?.data?.message || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      console.error("Send OTP Error:", error);
      setVerificationError(error?.response?.data?.message || "Failed to send OTP. Please check your network and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP API Integration
  const handleVerifyOTP = async () => {
    if (!otpValue || otpValue.length !== 6) {
      setVerificationError("Please enter a valid 6-digit OTP");
      return;
    }
    
    setLoading(true);
    
    try {
      // API call to verify OTP
      const response = await api.post("/verifyfrom-otp", {
        phone: formData.whatsapp,
        otp: otpValue
      });
      
      console.log("Verify OTP Response:", response);
      
      if (response?.data?.status === true || response?.status === 200) {
        setOtpVerified(true);
        setShowOtpInput(false);
        setVerificationError("");
        setOtpValue("");
        // Optional: Show success toast/notification
        console.log("WhatsApp number verified successfully!");
      } else {
        setVerificationError(response?.data?.message || "Invalid OTP. Please try again.");
        // Clear OTP input on failure
        setOtpValue("");
      }
    } catch (error) {
      console.error("Verify OTP Error:", error);
      setVerificationError(error?.response?.data?.message || "Verification failed. Please try again.");
      // Clear OTP input on failure
      setOtpValue("");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name) errors.name = "Name is required";
    if (!formData.email) errors.email = "Email is required";
    if (!formData.whatsapp) errors.whatsapp = "WhatsApp number is required";
    if (!otpVerified) errors.whatsapp = "WhatsApp number must be verified";
    if (!formData.destination) errors.destination = "Destination is required";
    if (!formData.pickup) errors.pickup = "Pickup location is required";
    if (!formData.drop) errors.drop = "Drop location is required";
    if (!formData.adult || formData.adult < 1) errors.adult = "At least 1 adult is required";
    if (!formData.days || formData.days < 1) errors.days = "Number of days is required";
    if (!formData.nights || formData.nights < 1) errors.nights = "Number of nights is required";
    if (!formData.startDate) errors.startDate = "Start date is required";
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('.tripform-error-message');
      if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    const submittedData = {
      type: planType,
      destination: formData.destination,
      pickup: formData.pickup,
      drop_point: formData.drop,
      adult: parseInt(formData.adult) || 0,
      child: parseInt(formData.child) || 0,
      infant: parseInt(formData.infant) || 0,
      no_of_days: parseInt(formData.days) || 0,
      no_of_nights: parseInt(formData.nights) || 0,
      start_date: formData.startDate ? dayjs(formData.startDate).format("YYYY-MM-DD") : null,
      travel: formData.travel,
      food: formData.food,
      special_request: formData.special,
      name: formData.name,
      email: formData.email,
      phone: formData.whatsapp, // Using whatsapp as phone
      whatsapp_verified: otpVerified,
      itineraries: Itinerary.filter(item => item.trim() !== "") // Remove empty itineraries
    };
    
    console.log("submittedData: ", submittedData);
    
    try {
      setLoading(true);
      const res = await api.post("/budget-requests", submittedData);
      console.log("Submit Response:", res);
      
      if (res?.data?.status === true) {
        setLoading(false);
        setOpen(true);
      } else {
        // Handle submission error
        alert(res?.data?.message || "Failed to submit request. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      console.error("Submit Error:", err);
      alert(err?.response?.data?.message || "Failed to submit request. Please check your network and try again.");
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Fullspin />}
      <div className="tripform-wrapper">
        <Navber />
        
        {/* Hero Section */}
        <div className="tripform-hero-section">
          <div className="tripform-hero-overlay"></div>
          <div className="tripform-hero-content tripform-fade-in-up">
            <h1 className="tripform-hero-title">Plan Your Dream Trip ✈️</h1>
            <p className="tripform-hero-subtitle">Personalized itinerary crafted just for you</p>
          </div>
        </div>

        {/* Success Modal */}
        {open && (
          <div className="tripform-modal-overlay" onClick={() => setOpen(false)}>
            <div className="tripform-modal-content tripform-slide-in" onClick={e => e.stopPropagation()}>
              <div className="tripform-success-icon">✓</div>
              <h2 className="tripform-modal-title">🎉 Submitted Successfully!</h2>
              <p className="tripform-modal-text">Your itinerary request has been sent. We'll contact you on WhatsApp within 24 hours.</p>
              <button className="tripform-modal-close-btn" onClick={() => {
                setOpen(false);
                window.location.reload();
              }}>Close</button>
            </div>
          </div>
        )}

        {/* Form Card */}
        <div className="tripform-card tripform-slide-in-up">
          {/* Toggle Buttons */}
          <div className="tripform-toggle-container">
            <button 
              className={`tripform-toggle-btn ${planType === 'budget_itinerary' ? 'tripform-active' : ''}`}
              onClick={() => handlePlanChange('budget_itinerary')}
            >
              Budget + Itinerary
            </button>
            <button 
              className={`tripform-toggle-btn ${planType === 'budget_basic' ? 'tripform-active' : ''}`}
              onClick={() => handlePlanChange('budget_basic')}
            >
              Only Budget
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="tripform-form-grid">
              {/* Basic Information */}
              <div className="tripform-form-group">
                <label className="tripform-form-label">Destination *</label>
                <input
                  type="text"
                  name="destination"
                  className={`tripform-form-input ${animatedFields.destination ? 'tripform-pulse-animation' : ''} ${formErrors.destination ? 'tripform-error' : ''}`}
                  value={formData.destination}
                  onChange={handleChange}
                  placeholder="Enter your destination"
                />
                {formErrors.destination && <span className="tripform-error-message">{formErrors.destination}</span>}
              </div>

              <div className="tripform-form-group">
                <label className="tripform-form-label">Pickup Location *</label>
                <input
                  type="text"
                  name="pickup"
                  className={`tripform-form-input ${animatedFields.pickup ? 'tripform-pulse-animation' : ''} ${formErrors.pickup ? 'tripform-error' : ''}`}
                  value={formData.pickup}
                  onChange={handleChange}
                  placeholder="Where should we pick you up?"
                />
                {formErrors.pickup && <span className="tripform-error-message">{formErrors.pickup}</span>}
              </div>

              <div className="tripform-form-group">
                <label className="tripform-form-label">Drop Location *</label>
                <input
                  type="text"
                  name="drop"
                  className={`tripform-form-input ${animatedFields.drop ? 'tripform-pulse-animation' : ''} ${formErrors.drop ? 'tripform-error' : ''}`}
                  value={formData.drop}
                  onChange={handleChange}
                  placeholder="Where should we drop you?"
                />
                {formErrors.drop && <span className="tripform-error-message">{formErrors.drop}</span>}
              </div>

              {/* Travelers Information */}
              <div className="tripform-form-row">
                <div className="tripform-form-group">
                  <label className="tripform-form-label">Adults (4yrs+) *</label>
                  <input
                    type="number"
                    name="adult"
                    min="1"
                    className={`tripform-form-input ${formErrors.adult ? 'tripform-error' : ''}`}
                    value={formData.adult}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        adult: value < 1 ? 1 : value,
                      }));
                    }}
                  />
                  {formErrors.adult && <span className="tripform-error-message">{formErrors.adult}</span>}
                </div>

                <div className="tripform-form-group">
                  <label className="tripform-form-label">Children (0-4yrs)</label>
                  <input
                    type="number"
                    name="child"
                    min="0"
                    className="tripform-form-input"
                    value={formData.child}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        child: value < 0 ? 0 : value,
                      }));
                    }}
                  />
                </div>
              </div>

              {/* Duration */}
              <div className="tripform-form-row">
                <div className="tripform-form-group">
                  <label className="tripform-form-label">Total Days *</label>
                  <input
                    type="number"
                    name="days"
                    min="1"
                    className={`tripform-form-input ${formErrors.days ? 'tripform-error' : ''}`}
                    value={formData.days}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        days: value < 1 ? 1 : value,
                      }));
                    }}
                  />
                  {formErrors.days && <span className="tripform-error-message">{formErrors.days}</span>}
                </div>

                <div className="tripform-form-group">
                  <label className="tripform-form-label">Total Nights *</label>
                  <input
                    type="number"
                    name="nights"
                    min="1"
                    className={`tripform-form-input ${formErrors.nights ? 'tripform-error' : ''}`}
                    value={formData.nights}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        nights: value < 1 ? 1 : value,
                      }));
                    }}
                  />
                  {formErrors.nights && <span className="tripform-error-message">{formErrors.nights}</span>}
                </div>
              </div>

              {/* Start Date */}
              <div className="tripform-form-group">
                <label className="tripform-form-label">Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  className={`tripform-form-input ${formErrors.startDate ? 'tripform-error' : ''}`}
                  value={formData.startDate ? dayjs(formData.startDate).format('YYYY-MM-DD') : ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, startDate: new Date(e.target.value) }))}
                  min={dayjs().format('YYYY-MM-DD')}
                />
                {formErrors.startDate && <span className="tripform-error-message">{formErrors.startDate}</span>}
              </div>

              {/* WhatsApp with Verification */}
              <div className="tripform-form-group">
                <label className="tripform-form-label">WhatsApp Number *</label>
                <div className="tripform-whatsapp-container">
                  <input
                    type="tel"
                    name="whatsapp"
                    className={`tripform-form-input tripform-whatsapp-input ${formErrors.whatsapp ? 'tripform-error' : ''} ${otpVerified ? 'tripform-verified' : ''}`}
                    value={formData.whatsapp}
                    onChange={handleChange}
                    placeholder="Enter 10-digit WhatsApp number"
                    disabled={otpVerified}
                  />
                  {!otpVerified && (
                    <button
                      type="button"
                      className="tripform-send-otp-btn"
                      onClick={handleSendOTP}
                      disabled={loading || !formData.whatsapp || countdown > 0}
                    >
                      {countdown > 0 ? `Resend in ${countdown}s` : (loading ? 'Sending...' : 'Send OTP')}
                    </button>
                  )}
                  {otpVerified && (
                    <div className="tripform-verified-badge">
                      <span className="tripform-verified-icon">✓</span>
                      <span>Verified</span>
                    </div>
                  )}
                </div>
                {verificationError && <span className="tripform-error-message">{verificationError}</span>}
                {formErrors.whatsapp && <span className="tripform-error-message">{formErrors.whatsapp}</span>}
              </div>

              {/* OTP Input */}
              {showOtpInput && !otpVerified && (
                <div className="tripform-form-group tripform-otp-section tripform-slide-down">
                  <label className="tripform-form-label">Enter OTP</label>
                  <div className="tripform-otp-container">
                    <input
                      type="text"
                      className="tripform-form-input tripform-otp-input"
                      value={otpValue}
                      onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      maxLength="6"
                    />
                    <button
                      type="button"
                      className="tripform-verify-otp-btn"
                      onClick={handleVerifyOTP}
                      disabled={loading || !otpValue}
                    >
                      {loading ? 'Verifying...' : 'Verify'}
                    </button>
                  </div>
                </div>
              )}

              {/* Personal Information */}
              <div className="tripform-form-row">
                <div className="tripform-form-group">
                  <label className="tripform-form-label">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    className={`tripform-form-input ${animatedFields.name ? 'tripform-pulse-animation' : ''} ${formErrors.name ? 'tripform-error' : ''}`}
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                  />
                  {formErrors.name && <span className="tripform-error-message">{formErrors.name}</span>}
                </div>

                <div className="tripform-form-group">
                  <label className="tripform-form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    className={`tripform-form-input ${formErrors.email ? 'tripform-error' : ''}`}
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                  />
                  {formErrors.email && <span className="tripform-error-message">{formErrors.email}</span>}
                </div>
              </div>

              {/* Itinerary Section (for Budget + Itinerary) */}
              {planType === "budget_itinerary" && (
                <div className="tripform-itinerary-section tripform-fade-in">
                  <h3 className="tripform-section-title">📅 Daily Itinerary</h3>
                  {days.map((item, index) => (
                    <div key={index} className="tripform-itinerary-item">
                      <textarea
                        className="tripform-itinerary-input"
                        placeholder={`Activities for Day ${item.day}`}
                        value={item.value}
                        onChange={(e) => handleChanges(index, e.target.value)}
                        rows="2"
                      />
                    </div>
                  ))}
                  <button type="button" className="tripform-add-day-btn" onClick={handleAddDay}>
                    + Add Another Day
                  </button>
                </div>
              )}

              {/* Preferences (for Only Budget) */}
              {planType === "budget_basic" && (
                <div className="tripform-form-row tripform-fade-in">
                  <div className="tripform-form-group">
                    <label className="tripform-form-label">Travel Preference</label>
                    <input
                      type="text"
                      name="travel"
                      className="tripform-form-input"
                      value={formData.travel}
                      onChange={handleChange}
                      placeholder="e.g., Flight, Train, Bus"
                    />
                  </div>
                  <div className="tripform-form-group">
                    <label className="tripform-form-label">Food Preference</label>
                    <input
                      type="text"
                      name="food"
                      className="tripform-form-input"
                      value={formData.food}
                      onChange={handleChange}
                      placeholder="e.g., Vegetarian, Non-vegetarian"
                    />
                  </div>
                </div>
              )}

              {/* Special Request */}
              <div className="tripform-form-group">
                <label className="tripform-form-label">Special Requests</label>
                <textarea
                  name="special"
                  className="tripform-form-textarea"
                  value={formData.special}
                  onChange={handleChange}
                  placeholder="Any special requirements or preferences..."
                  rows="4"
                />
              </div>

              {/* Submit Button */}
              <div className="tripform-submit-container">
                <button 
                  type="submit" 
                  className={`tripform-submit-btn ${!otpVerified ? 'tripform-disabled' : ''}`}
                  disabled={!otpVerified || loading}
                >
                  {loading ? 'Submitting...' : (!otpVerified ? 'Verify WhatsApp to Continue' : 'Send Request 🚀')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}